import React from 'react';
import { Card } from '../components/ui/card';
import { Printer, WifiHigh, CheckCircle, Warning, DeviceTablet, ArrowSquareOut } from '@phosphor-icons/react';

const RECOMMENDED_PRINTERS = [
    { name: 'Star micronics TSP100III WLAN', detail: 'Térmica 80mm · WiFi · AirPrint nativo', best: true },
    { name: 'Star mPOP', detail: 'Térmica 58mm + cajón · Bluetooth/USB · iOS' },
    { name: 'Epson TM-m30III', detail: 'Térmica 80mm · WiFi · AirPrint' },
    { name: 'Bixolon SRP-Q300', detail: 'Térmica 80mm · Bluetooth/USB' },
];

const PrinterSetupPage = () => {
    return (
        <div className="space-y-6 animate-fade-up max-w-3xl" data-testid="printer-setup-page">
            <div className="pb-6 border-b border-[#ECE3D2]">
                <div className="flex items-center gap-2 text-xs mb-2">
                    <span className="text-[#8B8474]">Configuración</span>
                    <span className="text-[#C7BCA5]">/</span>
                    <span className="text-[#1F2D4B] font-medium">Impresora de tickets</span>
                </div>
                <h1 className="font-display text-3xl font-bold text-[#1F2D4B]">Configurar impresora</h1>
                <p className="text-sm text-[#4B5878] mt-1.5">
                    Guía para imprimir tickets desde iPad usando <b>AirPrint</b>
                </p>
            </div>

            {/* Step 1 */}
            <Card className="bg-white border-[#ECE3D2] rounded-xl p-6">
                <div className="flex items-start gap-3 mb-3">
                    <div className="w-9 h-9 rounded-md bg-[#1F2D4B] text-white flex items-center justify-center font-bold flex-shrink-0">1</div>
                    <div>
                        <h2 className="font-display text-xl font-semibold text-[#1F2D4B]">Elige una impresora compatible con AirPrint</h2>
                        <p className="text-sm text-[#4B5878] mt-1">
                            AirPrint funciona nativamente en iPad/iPhone sin instalar nada. La mayoría de las impresoras
                            térmicas modernas para POS la soportan vía WiFi.
                        </p>
                    </div>
                </div>
                <div className="space-y-2 mt-4">
                    {RECOMMENDED_PRINTERS.map((p, i) => (
                        <div key={i} className={`p-3 rounded-md border ${p.best ? 'border-[#2D7A52] bg-[#E8F5E9]/40' : 'border-[#ECE3D2] bg-[#FAF6F0]/40'} flex items-start gap-3`}>
                            <Printer size={18} weight="duotone" className="text-[#1F2D4B] mt-0.5 flex-shrink-0" />
                            <div className="flex-1">
                                <div className="flex items-center gap-2">
                                    <p className="text-sm font-semibold text-[#1F2D4B]">{p.name}</p>
                                    {p.best && <span className="text-[9px] font-bold uppercase tracking-wider bg-[#2D7A52] text-white px-1.5 py-0.5 rounded">Recomendada</span>}
                                </div>
                                <p className="text-xs text-[#4B5878] mt-0.5">{p.detail}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </Card>

            {/* Step 2 */}
            <Card className="bg-white border-[#ECE3D2] rounded-xl p-6">
                <div className="flex items-start gap-3 mb-3">
                    <div className="w-9 h-9 rounded-md bg-[#1F2D4B] text-white flex items-center justify-center font-bold flex-shrink-0">2</div>
                    <div>
                        <h2 className="font-display text-xl font-semibold text-[#1F2D4B]">Conectar a la misma red WiFi</h2>
                        <p className="text-sm text-[#4B5878] mt-1">
                            Tu iPad y tu impresora <b>deben estar en la misma red WiFi</b> para que iOS la detecte.
                        </p>
                    </div>
                </div>
                <ol className="text-sm text-[#4B5878] space-y-2 ml-12 list-decimal">
                    <li>Enciende la impresora y conéctala a tu WiFi (consulta el manual del fabricante).</li>
                    <li>En el iPad: <b>Ajustes → WiFi</b> y verifica la red.</li>
                    <li>Abre cualquier pantalla con el ícono compartir, pulsa <b>Imprimir</b> y selecciona tu impresora.</li>
                </ol>
            </Card>

            {/* Step 3 */}
            <Card className="bg-white border-[#ECE3D2] rounded-xl p-6">
                <div className="flex items-start gap-3 mb-3">
                    <div className="w-9 h-9 rounded-md bg-[#1F2D4B] text-white flex items-center justify-center font-bold flex-shrink-0">3</div>
                    <div>
                        <h2 className="font-display text-xl font-semibold text-[#1F2D4B]">Imprimir un ticket desde la app</h2>
                        <p className="text-sm text-[#4B5878] mt-1">
                            Cuando completes una venta o desde el listado de Ventas, pulsa el botón <b>Imprimir ticket</b>.
                        </p>
                    </div>
                </div>
                <div className="ml-12 space-y-2">
                    <p className="text-sm text-[#4B5878] flex items-center gap-2"><CheckCircle size={14} weight="bold" className="text-[#2D7A52]" /> El ticket se renderiza en 58mm (tamaño térmico estándar).</p>
                    <p className="text-sm text-[#4B5878] flex items-center gap-2"><CheckCircle size={14} weight="bold" className="text-[#2D7A52]" /> Incluye QR para que el cliente vea su certificado en línea.</p>
                    <p className="text-sm text-[#4B5878] flex items-center gap-2"><CheckCircle size={14} weight="bold" className="text-[#2D7A52]" /> Funciona también desde el listado <b>Ventas</b> con el ícono de impresora para reimprimir.</p>
                </div>
            </Card>

            {/* Troubleshooting */}
            <Card className="bg-[#FFF8E7] border-[#F5DEB3] rounded-xl p-6">
                <div className="flex items-start gap-3">
                    <Warning size={22} weight="duotone" className="text-[#92400E] flex-shrink-0" />
                    <div>
                        <h3 className="font-display text-base font-semibold text-[#92400E] mb-2">¿Mi impresora no aparece en el diálogo de impresión?</h3>
                        <ul className="text-sm text-[#7A4F00] space-y-1.5 list-disc pl-4">
                            <li>Verifica que la impresora soporte AirPrint (debe decirlo en el empaque o ficha técnica).</li>
                            <li>Reinicia router, iPad e impresora — a veces el dispositivo no se publica en mDNS.</li>
                            <li>Si tu impresora NO tiene AirPrint, usa una app puente como <b>Printopia</b> (Mac) o <b>handyPrint</b>, o un servidor de impresión Raspberry Pi con CUPS.</li>
                            <li>Para impresoras Star, descarga la app <i>Star Quick Setup Utility</i> y habilita IPP/AirPrint en la configuración.</li>
                        </ul>
                    </div>
                </div>
            </Card>

            {/* Tablet/iPad tip */}
            <Card className="bg-[#F0F7FF] border-[#BFDBFE] rounded-xl p-6">
                <div className="flex items-start gap-3">
                    <DeviceTablet size={22} weight="duotone" className="text-[#1E40AF] flex-shrink-0" />
                    <div>
                        <h3 className="font-display text-base font-semibold text-[#1E40AF] mb-1">iPad como POS principal</h3>
                        <p className="text-sm text-[#1E40AF]/80">
                            Esta app está optimizada para iPad. Para mejor experiencia: añade el sitio a la pantalla de inicio
                            (<i>Safari → compartir → Añadir a pantalla de inicio</i>) y se comportará como una app nativa
                            en modo apaisado/horizontal.
                        </p>
                    </div>
                </div>
            </Card>
        </div>
    );
};

export default PrinterSetupPage;
